import { Fragment, ReactNode, useEffect, useMemo, useRef, useState } from 'react'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ListItemButtonWithNavigation from '../input/ListItemButtonWithNavigation'
import { matchPath, useLocation } from 'react-router-dom'

export type DrawerItem = {
  icon?: ReactNode
  text: string
  key: string
  path: string
  activePaths?: readonly string[]
}

export type DrawerSection = {
  title: string
  id?: string
  items: readonly DrawerItem[]
  dividerBefore?: boolean
}

export type DrawerScaffoldProps = {
  sections: readonly DrawerSection[]
  children: ReactNode
  footerContent?: ReactNode
  overlayNavigation?: boolean
  navigationOpen?: boolean
  onNavigationClose?: () => void
}

const getSectionKey = (section: DrawerSection, index: number) =>
  section.id ?? `${section.title.toLowerCase().replace(/\s+/g, '-')}-${index}`

const DRAWER_WIDTH_PX = 240

const DrawerScaffold = ({
  sections,
  children,
  footerContent,
  overlayNavigation = false,
  navigationOpen = false,
  onNavigationClose,
}: DrawerScaffoldProps) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const location = useLocation()
  const previousPathRef = useRef(location.pathname)

  const isPathActive = (path: string) =>
    !!matchPath(
      {
        path,
        end: false,
      },
      location.pathname
    )

  const resolveActivePaths = (item: DrawerItem) =>
    item.activePaths && item.activePaths.length > 0 ? [item.path, ...item.activePaths] : [item.path]

  const isItemActive = (item: DrawerItem) => resolveActivePaths(item).some((path) => isPathActive(path))

  const grouped = useMemo(() => {
    const visibleSections = sections.filter((section) => section.items.length > 0)
    return visibleSections.map((section, index) => ({
      ...section,
      key: getSectionKey(section, index),
      dividerBefore:
        section.dividerBefore ??
        (index > 0 && visibleSections.slice(0, index).some((prevSection) => prevSection.items.length > 0)),
    }))
  }, [sections])

  const toggle = (key: string, expandedState: boolean) =>
    setExpanded((prev) => ({
      ...prev,
      [key]: expandedState,
    }))

  useEffect(() => {
    if (location.pathname === previousPathRef.current) return
    previousPathRef.current = location.pathname
    if (overlayNavigation && navigationOpen) {
      onNavigationClose?.()
    }
  }, [location.pathname, navigationOpen, onNavigationClose, overlayNavigation])

  const drawerVariant = overlayNavigation ? 'temporary' : 'permanent'
  const handleItemNavigate = overlayNavigation ? onNavigationClose : undefined

  return (
    <Box className="drawerScaffold">
      <Drawer
        variant={drawerVariant}
        open={overlayNavigation ? navigationOpen : true}
        onClose={overlayNavigation ? onNavigationClose : undefined}
        ModalProps={overlayNavigation ? { keepMounted: true } : undefined}
        PaperProps={{
          elevation: 2,
          sx: { width: `${DRAWER_WIDTH_PX}px`, bgcolor: 'background.default', color: 'text.primary' },
        }}
        sx={{ width: `${DRAWER_WIDTH_PX}px`, flexShrink: 0 }}
        className="drawer"
      >
        <Stack sx={{ height: '100%' }}>
          <Toolbar />
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {grouped.map(({ title, items, dividerBefore, key }) => {
              const sectionHasActiveItem = items.some((item) => isItemActive(item))
              const isExpanded = expanded[key] ?? sectionHasActiveItem
              return (
                <Fragment key={key}>
                  {dividerBefore && <Divider />}
                  <Accordion
                    expanded={isExpanded}
                    onChange={(event, expandedState) => toggle(key, expandedState)}
                    disableGutters
                    square
                    elevation={0}
                    sx={{
                      bgcolor: 'transparent',
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                      aria-controls={`${key}-content`}
                      id={`${key}-header`}
                      sx={{
                        px: 2,
                        py: 0,
                        minHeight: 18,
                        '& .MuiAccordionSummary-content': {
                          margin: 0,
                          alignItems: 'center',
                        },
                      }}
                    >
                      <ListItemText
                        primary={title}
                        primaryTypographyProps={{
                          variant: 'overline',
                          letterSpacing: 1.5,
                          fontSize: '0.7rem',
                          color: sectionHasActiveItem ? 'primary' : undefined,
                        }}
                      />
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <List disablePadding>
                        {items.map(({ icon, text, key: itemKey, path, activePaths }) => (
                          <ListItem key={`item-${itemKey}`} disablePadding>
                            <ListItemButtonWithNavigation
                              path={path}
                              activePaths={activePaths}
                              onNavigate={handleItemNavigate}
                            >
                              {icon && <ListItemIcon sx={{ minWidth: 30 }}>{icon}</ListItemIcon>}
                              <ListItemText primary={text} primaryTypographyProps={{ fontSize: '0.82rem' }} />
                            </ListItemButtonWithNavigation>
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                </Fragment>
              )
            })}
          </Box>
          {footerContent && <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>{footerContent}</Box>}
        </Stack>
      </Drawer>
      <Stack sx={{ flexGrow: 1, overflowX: 'hidden', bgcolor: 'background.default', color: 'text.primary' }}>
        {children}
      </Stack>
    </Box>
  )
}

export default DrawerScaffold
